<template>
	<div class="fiscal-period-selector" ref="watingZone" tabindex="101" startup>
		<div class="fps-header">
			<button type="button" class="fps-close" @click="dismissSelector" :aria-label="$t('dialog.cancel')">
				<icon :icon="['fas', 'times']" />
			</button>
			<h2 class="fps-title">{{ $t('fiscal.selector.title') }}</h2>
			<p class="fps-subtitle">{{ $t('fiscal.selector.subtitle') }}</p>
		</div>

		<div class="fps-search">
			<input v-model="searchInput" type="text" class="fps-search-input" :placeholder="$t('fiscal.selector.search-placeholder')" @input="onSearchInput" @keydown.enter.prevent="submit" @keydown.stop />
			<icon icon="search" class="fps-search-icon" />
		</div>

		<div class="fps-companies">
			<div class="fps-companies-inner">
				<div v-for="group in visibleGroups" :key="group.company.id" class="fps-company-card" :class="{
					'fps-company-card--expanded': isCompanyExpanded(group.company.id),
					'fps-company-card--active': group.company.id === currentCompanyId
				}">
					<button type="button" class="fps-company-header" @click="toggleCompany(group.company.id)">
						<div class="fps-company-info">
							<div class="fps-company-name-row">
								<span class="fps-company-name">{{ group.company.name }}</span>
								<span v-if="group.company.id === currentCompanyId" class="fps-badge fps-badge--active">{{ $t('fiscal.selector.active-company') }}</span>
							</div>
						</div>
						<icon :icon="isCompanyExpanded(group.company.id) ? 'chevron-up' : 'chevron-down'" class="fps-company-chevron" :class="{ 'fps-company-chevron--expanded': isCompanyExpanded(group.company.id) }" />
					</button>

					<div v-if="isCompanyExpanded(group.company.id)" class="fps-periods-wrap">
						<div class="fps-periods">
							<p class="fps-periods-label">{{ $t('fiscal.selector.periods-label') }}</p>
							<div class="fps-period-list">
								<button v-for="period in group.periods" :key="period.id" type="button" class="fps-period-card" :class="{
									'fps-period-card--selected': selectedPeriodId === period.id,
									'fps-period-card--current': period.id === currentPeriodId,
									'fps-period-card--closed': period.closed && period.id !== currentPeriodId
								}" @click="selectPeriod(period)" @dblclick.prevent="onPeriodDblClick(period)">
									<div class="fps-period-card-main">
										<div class="fps-period-icon" :class="{
											'fps-period-icon--current': period.id === currentPeriodId,
											'fps-period-icon--selected': selectedPeriodId === period.id && period.id !== currentPeriodId
										}">
											<icon :icon="period.id === currentPeriodId ? 'calendar-check' : 'calendar'" />
										</div>
										<div class="fps-period-text">
											<span class="fps-period-name">{{ period.name }}</span>
											<span class="fps-period-dates">{{ period.dateRange }}</span>
										</div>
									</div>
									<div class="fps-period-badges">
										<span v-if="period.id === currentPeriodId" class="fps-badge fps-badge--current">
											<span class="fps-badge-dot"></span>
											{{ $t('fiscal.selector.current-period') }}
										</span>
										<span v-else-if="period.closed" class="fps-badge fps-badge--closed">
											{{ $t('fiscal.f.closed') }}
										</span>
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-if="visibleGroups.length === 0" class="fps-empty">
				{{ $t('fiscal.selector.no-results') }}
			</div>
		</div>

		<div class="fps-submit-wrap">
			<button type="button" class="fps-submit" :disabled="!selectedPeriod" @click="submit">
				<span>{{ $t('fiscal.selector.select-period') }}</span>
				<icon icon="arrow-left" class="fps-submit-icon" />
			</button>
		</div>
	</div>
</template>

<script>
	import _ from 'lodash'
	import $sc from '~/plugins/shortcut.collection.js'
	import jalaliMoment from 'jalali-moment'
	import { mapGetters, mapState } from 'vuex'
	import dialog from '~/plugins/nikan-dialog-plugin'

	export default {
		name: 'fiscal-selector-old',
		modal: {
			size: 'sm',
			stateUID: 'fiscal-selector',
			helpTag: 'FiscalSelector'
		},
		props: ['options'],
		data() {
			const defaultSettings = {
				onlyCurrentCopmany: false,
				excludeCurrentPeriod: false,
				confirm: false,
			}

			return {
				fiscals: [],
				displayGroups: [],
				selectedPeriod: null,
				selectedPeriodId: null,
				searchInput: '',
				searchQuery: '',
				expandedCompanyIds: [],
				expandedCompanyMap: {},
				defaultSettings,
				settings: { ...defaultSettings },
			}
		},
		created() {
			this.applySearchDebounced = _.debounce(this.applySearch, 200)
		},
		beforeUnmount() {
			this.applySearchDebounced?.cancel()
		},
		watch: {
			options: {
				immediate: true,
				handler(val) {
					this.settings = { ...this.defaultSettings, ...(val || {}) }
					this.getTableData()
				},
			},
		},
		computed: {
			...mapState('appContext', {
				status: (state) => state,
			}),
			...mapGetters({
				allFiscalPeriod: 'fiscalPeriods/all'
			}),
			currentCompanyId() {
				return this.status.company?.id
			},
			currentPeriodId() {
				return this.status.fiscalPeriod?.id
			},
			visibleGroups() {
				const query = this.searchQuery
				if (!query)
					return this.displayGroups

				const filtered = this.displayGroups
					.map((group) => {
						if (group.companySearchKey.includes(query))
							return group

						const periods = group.periods.filter(period => period.searchKey.includes(query))
						if (periods.length) {
							return {
								...group,
								periods: this.sortPeriods(periods),
							}
						}

						return null
					})
					.filter(Boolean)

				return this.sortCompanyGroups(filtered)
			},
		},
		mounted() {
			this.prepareWindow(this.$t('fiscal.selector.title'), [])
			this.getTableData()
			this.selectedPeriod = this.data || this.status.fiscalPeriod || null
			this.selectedPeriodId = this.selectedPeriod?.id ?? null
			this.setExpandedCompanyIds(this.getDefaultExpandedCompanyIds())
		},
		methods: {
			getTableData() {
				if (!this.$nikan.isZeroLength(this.allFiscalPeriod)) {
					let fiscals = this.allFiscalPeriod

					if (this.settings.onlyCurrentCopmany)
						fiscals = fiscals.filter(x => x.companyId == this.status.company.id)

					if (this.settings.excludeCurrentPeriod)
						fiscals = fiscals.filter(x => x.id != this.status.fiscalPeriod.id)

					this.fiscals = fiscals
					this.rebuildDisplayGroups()
				}
			},
			rebuildDisplayGroups() {
				const groups = new Map()

				this.fiscals.forEach((period) => {
					if (!period?.company?.id)
						return

					const companyId = period.companyId
					if (!groups.has(companyId)) {
						groups.set(companyId, {
							company: period.company,
							periods: [],
						})
					}

					const dateRange = this.formatPeriodRange(period)
					groups.get(companyId).periods.push({
						...period,
						dateRange,
						searchKey: this.normalizeSearchText(`${period.name || ''} ${dateRange}`),
					})
				})

				const displayGroups = Array.from(groups.values()).map((group) => {
					const periods = this.sortPeriods(group.periods)
					const companySearchKey = this.normalizeSearchText(group.company.name || '')

					return {
						...group,
						periods,
						companySearchKey,
						searchKey: `${companySearchKey} ${periods.map(p => p.searchKey).join(' ')}`.trim(),
					}
				})

				this.displayGroups = this.sortCompanyGroups(displayGroups)
			},
			sortCompanyGroups(groups) {
				const currentCompanyId = this.currentCompanyId

				return [...groups].sort((left, right) => {
					if (left.company.id == currentCompanyId)
						return -1
					if (right.company.id == currentCompanyId)
						return 1

					return (left.company.name || '').localeCompare(right.company.name || '', 'fa')
				})
			},
			sortPeriods(periods) {
				const currentId = this.currentPeriodId

				return [...periods].sort((left, right) => {
					if (left.id == currentId)
						return -1
					if (right.id == currentId)
						return 1

					const startCompare = String(left.startDate || '').localeCompare(String(right.startDate || ''))
					if (startCompare !== 0)
						return startCompare

					return (left.name || '').localeCompare(right.name || '', 'fa')
				})
			},
			setExpandedCompanyIds(ids) {
				this.expandedCompanyIds = ids
				this.expandedCompanyMap = ids.reduce((map, id) => {
					map[id] = true
					return map
				}, {})
			},
			getDefaultExpandedCompanyIds() {
				return this.currentCompanyId ? [this.currentCompanyId] : []
			},
			getExpandedCompanyIdsForQuery(query) {
				return this.displayGroups
					.filter((group) => {
						if (group.companySearchKey.includes(query))
							return true

						return group.periods.some(period => period.searchKey.includes(query))
					})
					.map(group => group.company.id)
			},
			normalizeSearchText(text) {
				return String(text || '').trim().toLowerCase().toEnglishDigits()
			},
			onSearchInput() {
				this.applySearchDebounced()
			},
			applySearch() {
				const query = this.normalizeSearchText(this.searchInput)
				this.searchQuery = query

				if (!query) {
					this.setExpandedCompanyIds(this.getDefaultExpandedCompanyIds())
					return
				}

				this.setExpandedCompanyIds(this.getExpandedCompanyIdsForQuery(query))
			},
			isCompanyExpanded(companyId) {
				return !!this.expandedCompanyMap[companyId]
			},
			toggleCompany(companyId) {
				if (this.isCompanyExpanded(companyId)) {
					const ids = this.expandedCompanyIds.filter(id => id !== companyId)
					this.setExpandedCompanyIds(ids)
					return
				}

				this.setExpandedCompanyIds([...this.expandedCompanyIds, companyId])
			},
			selectPeriod(period) {
				this.selectedPeriod = period
				this.selectedPeriodId = period?.id ?? null
			},
			onPeriodDblClick(period) {
				this.selectPeriod(period)
				this.rowDbClicked(period)
			},
			formatDate(date) {
				if (!date || String(date).includes('0001-01-01'))
					return ''

				const parsed = jalaliMoment(date, 'YYYY-M-D')
				if (!parsed.isValid())
					return ''

				return parsed.locale('fa').format('YYYY/MM/DD').toPersianDigits()
			},
			formatPeriodRange(period) {
				if (!period)
					return ''

				const startDate = this.formatDate(period.startDate)
				const endDate = this.formatDate(period.endDate)

				if (!startDate && !endDate)
					return ''

				return `${startDate} - ${endDate}`
			},
			submit() {
				if (_.isEmpty(this.selectedPeriod))
					return this.$toast(this.$t('document.m.select-row'))

				this.rowDbClicked(this.selectedPeriod)
			},
			dismissSelector() {
				this.$emit('close-button-clicked')
			},
			finishSelection(val) {
				this.resolve({
					...val,
					fiscalName: val.name,
				}, false)
			},
			rowDbClicked(val = this.selectedPeriod) {
				if (val.id === this.status.fiscalPeriod?.id) {
					this.$toast(this.$t('fiscal.v.selected-fiscal-is-current-fiscal'), { type: 'error' })
					return
				}

				if (!this.settings.confirm) {
					dialog({
						title: this.$t('notifications.titles-attention'),
						body: this.$t('account.copy-from-other-periods.are-you-sure', {
							period: val.name,
							company: val.company.name
						}),
						autoClose: true,
						buttons: [
							{
								title: this.$t('dialog.yes'),
								shortcut: $sc.find('yes', 'dialog'),
								isPrimary: true,
								handle: () => {
									this.finishSelection(val)
								},
							},
							{
								title: this.$t('dialog.cancel'),
								shortcut: $sc.find('cancel', 'dialog'),
								isCancel: true,
								handle: () => { },
							},
						],
					})
				} else {
					this.finishSelection(val)
				}
			},
		},
	}
</script>

<style lang="less" scoped>
	@import '~/styles/site.config.less';

	.fiscal-period-selector {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		padding-top: 12px;
		background: #fff;
		color: #1f2937;
	}

	.fps-header {
		position: relative;
		padding-left: 40px;
		padding-right: 12px;
		margin-bottom: 10px;
	}

	.fps-close {
		position: absolute;
		top: 0;
		left: 12px;
		width: 24px;
		height: 24px;
		border: none;
		border-radius: 50%;
		background: #f3f4f6;
		color: #6b7280;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		font-size: 11px;
		transition: background-color 0.2s ease;

		&:hover {
			background: #e5e7eb;
		}
	}

	.fps-title {
		margin: 0 0 2px;
		font-size: 14px;
		font-weight: 700;
		color: #111827;
		line-height: 1.3;
	}

	.fps-subtitle {
		margin: 0;
		font-size: 11px;
		color: #9ca3af;
		line-height: 1.3;
	}

	.fps-search {
		position: relative;
		margin-bottom: 8px;
		padding-left: 12px;
		padding-right: 12px;
	}

	.fps-search-input {
		width: 100%;
		height: 32px;
		border: 1px solid #e5e7eb;
		border-radius: 10px;
		background: #f9fafb;
		padding: 0 32px 0 10px;
		font-size: 12px;
		outline: none;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;

		&:focus {
			border-color: fade(@primary, 45%);
			background: #fff;
			box-shadow: 0 0 0 2px fade(@primary, 10%);
		}
	}

	.fps-search-icon {
		position: absolute;
		top: 50%;
		right: 22px;
		transform: translateY(-50%);
		color: #9ca3af;
		font-size: 12px;
		pointer-events: none;
	}

	.fps-companies {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		padding-right: 12px;
		padding-left: 12px;
		padding-top: 12px;
		padding-bottom: 12px;
		background-color: #f8fafc;
	}

	.fps-companies-inner {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.fps-company-card {
		border: 1px solid #e5e7eb;
		border-radius: 10px;
		background: #fff;
		overflow: hidden;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;

		&:not(.fps-company-card--expanded) {
			.fps-company-header {
				padding: 8px 10px;
				min-height: 36px;
			}
		}

		&--active,
		&--expanded {
			border-color: fade(@primary, 45%);
			box-shadow: 0 2px 8px fade(@primary, 8%);

			.fps-company-header {
				background-color: #f9fbff;
				border-bottom: 1px solid #e5f0fe;
			}
		}
	}

	.fps-company-header {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 8px 10px;
		border: none;
		background: transparent;
		text-align: right;
		cursor: pointer;
	}

	.fps-company-info {
		min-width: 0;
		flex: 1;
	}

	.fps-company-name-row {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
		margin-bottom: 1px;
	}

	.fps-company-name {
		font-size: 12px;
		font-weight: 700;
		color: #111827;
	}

	.fps-company-chevron {
		font-size: 11px;
		color: #9ca3af;
		transition: transform 0.2s ease, color 0.2s ease;

		&--expanded {
			color: @primary;
			transform: rotate(180deg);
		}
	}

	.fps-periods-wrap {
		overflow: hidden;
	}

	.fps-periods {
		padding: 0 8px 8px;
	}

	.fps-periods-label {
		margin: 0;
		font-size: 10px;
		color: #9ca3af;
	}

	.fps-period-list {
		display: flex;
		flex-direction: column;
		gap: 5px;
	}

	.fps-period-card {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 7px 8px;
		border: 1px solid #e5e7eb;
		border-radius: 8px;
		background: #fff;
		text-align: right;
		cursor: pointer;
		transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

		&:hover {
			border-color: fade(@primary, 30%);
		}

		&--selected {
			border: 2px solid @primary;
			background: fade(@primary, 10%);
			box-shadow: 0 0 0 2px fade(@primary, 14%);
			padding: 6px 7px;
		}

		&--current {
			border-color: #22c55e;
			background: fade(#22c55e, 5%);
		}

		&--current.fps-period-card--selected {
			border: 2px solid #16a34a;
			background: fade(#22c55e, 12%);
			box-shadow: 0 0 0 2px fade(#22c55e, 16%);
		}

		&--closed {
			opacity: 0.92;
		}
	}

	.fps-period-card-main {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}

	.fps-period-icon {
		width: 26px;
		height: 26px;
		border-radius: 7px;
		background: #f3f4f6;
		color: #6b7280;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		font-size: 11px;
		transition: background-color 0.15s ease, color 0.15s ease;

		&--current {
			background: fade(#22c55e, 14%);
			color: #16a34a;
		}

		&--selected {
			background: fade(@primary, 16%);
			color: @primary;
		}
	}

	.fps-period-text {
		display: flex;
		flex-direction: column;
		gap: 1px;
		min-width: 0;
	}

	.fps-period-name {
		font-size: 12px;
		font-weight: 600;
		color: #111827;
		line-height: 1.3;
	}

	.fps-period-dates {
		font-size: 10px;
		color: #9ca3af;
		line-height: 1.3;
	}

	.fps-period-badges {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 4px;
		flex-shrink: 0;
	}

	.fps-badge {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 2px 7px;
		border-radius: 999px;
		font-size: 9px;
		font-weight: 600;
		white-space: nowrap;
		line-height: 1.4;

		&--active {
			background: fade(@primary, 12%);
			color: @primary;
		}

		&--current {
			background: fade(#22c55e, 12%);
			color: #15803d;
		}

		&--closed {
			background: #f3f4f6;
			color: #6b7280;
			border: 1px solid #e5e7eb;
		}
	}

	.fps-badge-dot {
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: currentColor;
	}

	.fps-empty {
		padding: 16px 8px;
		text-align: center;
		color: #9ca3af;
		font-size: 11px;
	}

	.fps-submit-wrap {
		padding: 0 12px;
	}

	.fps-submit {
		width: 100%;
		height: 34px;
		margin: 8px 0px;
		border: none;
		border-radius: 10px;
		background: @primary;
		color: #fff;
		font-size: 12px;
		font-weight: 600;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		cursor: pointer;
		transition: background-color 0.2s ease, opacity 0.2s ease;

		&:hover:not(:disabled) {
			background: darken(@primary, 6%);
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	.fps-submit-icon {
		font-size: 11px;
	}
</style>
